import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { api } from "@/api/client";
import BacktestComingSoonModal from "@/components/feature/BacktestComingSoon";

export default function AIStrategyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [strategy, setStrategy] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [showBacktestSoon, setShowBacktestSoon] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    api.get<any>(`/alpha-lab/strategies/${id}`)
      .then((d) => { if (d?.id) setStrategy(d); else setError("策略未找到"); })
      .catch(() => setError("加载失败"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDelete = async () => {
    if (!confirm("确定要删除此策略吗？")) return;
    setDeleting(true);
    try {
      await api.delete(`/alpha-lab/strategies/${id}`);
      navigate("/ai-strategy", { replace: true });
    } catch {
      alert("删除失败"); setDeleting(false);
    }
  };

  const handleUse = async () => {
    setSwitching(true);
    try {
      await api.post("/alpha-lab/sessions", { strategyId: Number(id), fundAmount: 500000 });
      navigate("/", { replace: true });
    } catch {
      alert("切换失败"); setSwitching(false);
    }
  };

  if (loading) return <div className="flex-1 flex items-center justify-center"><div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (error || !strategy) return <div className="flex-1 px-4 py-4"><p className="text-foreground-400 text-sm text-center py-12">{error || "策略未找到"}</p></div>;

  const s = strategy;
  const cfg = (() => { try { return JSON.parse(s.configParams || "{}"); } catch { return {}; } })();

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-s-line text-foreground-600 text-lg" />
        </button>
        <h1 className="text-lg font-bold text-foreground-950">策略详情</h1>
      </div>

      <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <span className="text-lg font-bold text-foreground-950">{s.name}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 ml-2">{s.style}</span>
          </div>
          <button onClick={() => navigate(`/ai-strategy/edit/${s.id}`)}
            className="text-[10px] px-3 py-1 rounded-full bg-primary-50 text-primary-600 cursor-pointer">编辑</button>
        </div>
        <p className="text-[10px] text-foreground-400 mb-2">{s.tags}</p>
        <p className="text-xs text-foreground-600">{s.description}</p>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={handleUse} disabled={switching}
          className="flex-1 py-2.5 rounded-lg text-xs font-semibold bg-primary-500 text-white cursor-pointer disabled:opacity-50">
          {switching ? "启用中..." : "使用此策略"}
        </button>
        <button onClick={() => setShowBacktestSoon(true)}
          className="flex-1 py-2.5 rounded-lg text-xs font-medium bg-background-100 text-foreground-600 cursor-pointer">
          回测
        </button>
        <button onClick={handleDelete} disabled={deleting}
          className="py-2.5 px-4 rounded-lg text-xs font-medium bg-red-50 text-red-500 cursor-pointer disabled:opacity-50">
          {deleting ? "删除中..." : "删除"}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white rounded-xl p-3 border border-background-200/60 text-center">
          <div className="text-[#E11D48] text-lg font-bold">{s.annualReturn || "--"}%</div>
          <div className="text-[10px] text-foreground-400">年化</div>
        </div>
        <div className="bg-white rounded-xl p-3 border border-background-200/60 text-center">
          <div className="text-[#10B981] text-lg font-bold">{Math.abs(s.maxDrawdown || 0)}%</div>
          <div className="text-[10px] text-foreground-400">回撤</div>
        </div>
        <div className="bg-white rounded-xl p-3 border border-background-200/60 text-center">
          <div className="text-foreground-950 text-lg font-bold">{s.holdingPeriodDays || "--"}天</div>
          <div className="text-[10px] text-foreground-400">周期</div>
        </div>
        <div className="bg-white rounded-xl p-3 border border-background-200/60 text-center">
          <div className="text-foreground-950 text-lg font-bold">{s.userCount || 0}</div>
          <div className="text-[10px] text-foreground-400">用户</div>
        </div>
      </div>

      {cfg.capital && (
        <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
          <h3 className="text-xs font-semibold text-foreground-950 mb-2">资金风控</h3>
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <div><span className="text-foreground-400">单仓上限</span><span className="float-right font-semibold">{cfg.capital.maxSinglePosPct || "--"}%</span></div>
            <div><span className="text-foreground-400">集中度</span><span className="float-right font-semibold">{cfg.capital.maxSectorConcPct || "--"}%</span></div>
            <div><span className="text-foreground-400">最低现金</span><span className="float-right font-semibold">{cfg.capital.cashFloorPct || "--"}%</span></div>
            {cfg.risk && <>
              <div><span className="text-foreground-400">止盈</span><span className="float-right font-semibold">{cfg.risk.takeProfitPct || "--"}%</span></div>
              <div><span className="text-foreground-400">止损</span><span className="float-right font-semibold">{cfg.risk.stopLossPct || "--"}%</span></div>
              <div><span className="text-foreground-400">回撤</span><span className="float-right font-semibold">{cfg.risk.maxDrawdownPct || "--"}%</span></div>
            </>}
          </div>
        </div>
      )}

      {cfg.stockPool && (
        <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
          <h3 className="text-xs font-semibold text-foreground-950 mb-2">股票池</h3>
          <div className="text-[11px] space-y-1">
            <div><span className="text-foreground-400">池：</span><span className="text-foreground-700">{(cfg.stockPool.pools || []).join("、") || "--"}</span></div>
            <div><span className="text-foreground-400">行业：</span><span className="text-foreground-700">{(cfg.stockPool.industriesAllow || []).join("、") || "不限"}</span></div>
            <div><span className="text-foreground-400">PE：</span><span className="text-foreground-700">{cfg.stockPool.peMin ?? "不限"} ~ {cfg.stockPool.peMax ?? "不限"}</span></div>
            <div><span className="text-foreground-400">市值：</span><span className="text-foreground-700">{cfg.stockPool.totalCapMinYi ?? "不限"} ~ {cfg.stockPool.totalCapMaxYi ?? "不限"}亿</span></div>
          </div>
        </div>
      )}
      <BacktestComingSoonModal open={showBacktestSoon} onClose={() => setShowBacktestSoon(false)} />
    </div>
  );
}
